import { useState } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { Champ, ChampSelection, ChampTexte, MentionObligatoire } from "@/composants/ui/champ";
import { useReferentiel } from "@/lib/referentiels";

/**
 * Le lot saisi — une seule fois, pour le découpage comme pour la correction.
 *
 * Un lot n'a plus d'adresse : c'est celle de sa commande (ADR-0031). Il a une
 * catégorie, toujours, du même référentiel que la commande : au découpage,
 * « chaque lot doit être catégorisé » (LAB, 23/09).
 */

export type ValeursLot = {
  intitule: string;
  categorie: string;
  montant_ht_quote_part: string | null;
  commentaires: string | null;
};

/** Un lot neuf, prérempli avec la catégorie de sa commande. */
export function lotVide(categorie: string): ValeursLot {
  return { intitule: "", categorie, montant_ht_quote_part: null, commentaires: null };
}

export type OptionCategorie = { valeur: string; libelle: string };

/**
 * Les catégories commande proposées, lues du référentiel administrable
 * (ADR-0030). AUCUNE n'est choisie d'office : « une valeur choisie par l'écran
 * est une valeur imposée par défaut » (ADR-0031, arbitrage LAB du 24/09).
 */
export function useCategoriesCommande() {
  const categories = useReferentiel("categories-commande", { actives: true });
  const options: OptionCategorie[] = (categories.donnees?.valeurs ?? []).map((v) => ({
    valeur: v.code,
    libelle: v.libelle,
  }));
  return { options, enCours: categories.enCours };
}

/**
 * En correction, la valeur courante peut être une catégorie désactivée depuis :
 * elle reste proposée, telle quelle, pour ne pas forcer un changement. Sans
 * valeur, l'option vide dit qu'il reste à choisir.
 */
export function optionsCategorie(
  categories: OptionCategorie[],
  valeur: string,
): OptionCategorie[] {
  const courantes =
    valeur && !categories.some((o) => o.valeur === valeur)
      ? [{ valeur, libelle: valeur }, ...categories]
      : categories;
  return valeur ? courantes : [{ valeur: "", libelle: "— Choisir —" }, ...courantes];
}

export function ChampsLot({
  valeurs,
  onChanger,
  categories,
  prefixe = "",
}: {
  valeurs: ValeursLot;
  onChanger: (valeurs: ValeursLot) => void;
  categories: OptionCategorie[];
  prefixe?: string;
}) {
  const poser = (champ: keyof ValeursLot) => (valeur: string) =>
    onChanger({ ...valeurs, [champ]: valeur === "" ? null : valeur });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Champ
        libelle="Intitulé"
        id={`${prefixe}intitule`}
        required
        value={valeurs.intitule}
        onChange={(e) => onChanger({ ...valeurs, intitule: e.target.value })}
        placeholder="Cuisine, Dressing…"
      />
      <ChampSelection
        libelle="Catégorie du lot"
        id={`${prefixe}categorie`}
        required
        options={optionsCategorie(categories, valeurs.categorie)}
        value={valeurs.categorie}
        onChange={(e) => onChanger({ ...valeurs, categorie: e.target.value })}
      />
      <Champ
        libelle="Quote-part HT"
        id={`${prefixe}quotepart`}
        type="number"
        min="0"
        step="0.01"
        aide="Facultative. La somme des lots n'a pas à égaler le montant de la Commande / Projet (§3.2)."
        className="sm:col-span-2"
        value={valeurs.montant_ht_quote_part ?? ""}
        onChange={(e) => poser("montant_ht_quote_part")(e.target.value)}
      />
      <ChampTexte
        libelle="Commentaires"
        id={`${prefixe}commentaires`}
        className="sm:col-span-2"
        value={valeurs.commentaires ?? ""}
        onChange={(e) => poser("commentaires")(e.target.value)}
      />
    </div>
  );
}

export function FormulaireLot({
  initial,
  onValider,
  onAnnuler,
  libelleValider = "Ajouter le lot",
}: {
  initial: ValeursLot;
  onValider: (valeurs: ValeursLot) => Promise<void>;
  onAnnuler: () => void;
  libelleValider?: string;
}) {
  const [valeurs, setValeurs] = useState(initial);
  const [envoi, setEnvoi] = useState(false);
  const categories = useCategoriesCommande();

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          await onValider(valeurs);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <MentionObligatoire />
      <ChampsLot valeurs={valeurs} onChanger={setValeurs} categories={categories.options} />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : libelleValider}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
